import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetFaqsAPI,
  GetFaqsByPlaceAPI,
  CreateFaqAPI,
  UpdateFaqAPI,
  DeleteFaqAPI,
  AssignFaqPlacesAPI,
  ReorderFaqsAPI,
} from "api/faqApi";

const handleThunkError = (err, rejectWithValue) => {
  const message = err?.response?.data?.message || err?.message || "Something went wrong";
  return rejectWithValue({ message });
};

export const getFaqs = createAsyncThunk("faq/getFaqs", async (params, { rejectWithValue }) => {
  try {
    const res = await GetFaqsAPI(params);
    return res.data;
  } catch (err) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const getFaqsByPlace = createAsyncThunk(
  "faq/getFaqsByPlace",
  async (placeId, { rejectWithValue }) => {
    try {
      const res = await GetFaqsByPlaceAPI(placeId);
      return res.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const createFaq = createAsyncThunk("faq/createFaq", async (data, { rejectWithValue }) => {
  try {
    const res = await CreateFaqAPI(data);
    return res.data;
  } catch (err) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const updateFaq = createAsyncThunk(
  "faq/updateFaq",
  async ({ faqId, data }, { rejectWithValue }) => {
    try {
      const res = await UpdateFaqAPI(faqId, data);
      return res.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const deleteFaq = createAsyncThunk("faq/deleteFaq", async (faqId, { rejectWithValue }) => {
  try {
    const res = await DeleteFaqAPI(faqId);
    return res.data;
  } catch (err) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const assignFaqPlaces = createAsyncThunk(
  "faq/assignFaqPlaces",
  async ({ faqId, placeIds }, { rejectWithValue }) => {
    try {
      const res = await AssignFaqPlacesAPI(faqId, placeIds);
      return res.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const reorderFaqs = createAsyncThunk(
  "faq/reorderFaqs",
  async ({ items, placeId }, { rejectWithValue }) => {
    try {
      const res = await ReorderFaqsAPI(items, placeId);
      return res.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

const faqSlice = createSlice({
  name: "faq",
  initialState: {
    items: [],
    total: 0,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(getFaqs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFaqs.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.items = payload?.faqs || [];
        state.total = payload?.total || 0;
      })
      .addCase(getFaqs.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(getFaqsByPlace.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFaqsByPlace.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.items = payload?.faqs || [];
        state.total = payload?.total || 0;
      })
      .addCase(getFaqsByPlace.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(createFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFaq.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createFaq.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(updateFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFaq.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateFaq.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(deleteFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteFaq.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteFaq.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(assignFaqPlaces.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignFaqPlaces.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignFaqPlaces.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(reorderFaqs.pending, (state) => {
        state.loading = true;
      })
      .addCase(reorderFaqs.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(reorderFaqs.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      }),
});

export default faqSlice.reducer;
